import React, { useEffect, useState } from 'react'
import { formatTextToNumbers, isExternalLink, isMobile } from '@cms/utils'
import { MapPin } from 'lucide-react'
import { Accordion } from '../Accordion'
import { BottomSheet } from '../BottomSheet'
import { Button, ButtonContainer } from './styled'
import { useStaticQuery, graphql } from 'gatsby'
import { Modal } from '../Modal'
import { createPortal } from 'react-dom'
import PropTypes from 'prop-types'

export const SchedulingModal = ({ isOpen, onClose, isHomeSched, defaultRegion, origin }) => {
  const data = useStaticQuery(graphql`
    query {
      wordpressWpGatsbyPage(slug: { eq: "fale-conosco" }) {
        acf {
          cards {
            first_card {
              title
              info
              sp_button_text
              rj_button_text
              df_button_text
            }
            second_card {
              title
              info
              sp_button_text
              rj_button_text
              df_button_text
            }
          }
        }
      }
      wordpressAltaThemeSettings {
        scheduling_link_rj
        scheduling_link_sp
        scheduling_link_df
        whatsapp_link_rj
        whatsapp_link_sp
        whatsapp_link_df
        phone_rj
        phone_sp
        phone_df
        home_sched_link_rj
        home_sched_link_sp
      }
    }
  `)

  const contactGatsbyPage = data.wordpressWpGatsbyPage.acf
  const settings = data.wordpressAltaThemeSettings

  const phoneCard = {
    title: contactGatsbyPage.cards.first_card.title,
    info: contactGatsbyPage.cards.first_card.info,
    spButtonText: contactGatsbyPage.cards.first_card.sp_button_text,
    spButtonLink: `tel:+55${formatTextToNumbers(settings.phone_sp)}`,
    rjButtonText: contactGatsbyPage.cards.first_card.rj_button_text,
    rjButtonLink: `tel:+55${formatTextToNumbers(settings.phone_rj)}`,
    dfButtonText: contactGatsbyPage.cards.first_card.df_button_text,
    dfButtonLink: `tel:+55${formatTextToNumbers(settings.phone_df)}`,
  }

  const whatsappCard = {
    title: contactGatsbyPage.cards.second_card.title,
    info: contactGatsbyPage.cards.second_card.info,
    spButtonText: contactGatsbyPage.cards.second_card.sp_button_text,
    spButtonLink: settings.whatsapp_link_sp,
    rjButtonText: contactGatsbyPage.cards.second_card.rj_button_text,
    rjButtonLink: settings.whatsapp_link_rj,
    dfButtonText: contactGatsbyPage.cards.second_card.df_button_text,
    dfButtonLink: settings.whatsapp_link_df,
  }

  const campaignNameMapping = {
    sp: {
      header: 'cta_alta-sp_site_cabecalho_agendar_conversao',
      hamburgerMenu: 'cta_alta-sp_site_menu_agendar_conversao',
      examShowcase: 'cta_alta-sp_site_vitrine-exames_agendar-domiciliar_conversao',
      vaccineShowcase: 'cta_alta-sp_site_vitrine-vacinas_agendar-domiciliar_conversao',
      examDetail: 'cta_alta-sp_site_pagina-produto-exames_agendar_conversao',
      vaccineDetail: 'cta_alta-sp_site_pagina-produto-vacinas_agendar_conversao',
    },
    rj: {
      header: 'cta_alta-rj_site_cabecalho_agendar_conversao',
      hamburgerMenu: 'cta_alta-rj_site_menu_agendar_conversao',
      examShowcase: 'cta_alta-rj_site_vitrine-exames_agendar-domiciliar_conversao',
      vaccineShowcase: 'cta_alta-rj_site_vitrine-vacinas_agendar-domiciliar_conversao',
      examDetail: 'cta_alta-rj_site_pagina-produto-exames_agendar_conversao',
      vaccineDetail: 'cta_alta-rj_site_pagina-produto-vacinas_agendar_conversao',
    },
    df: {
      header: 'cta_alta-df_site_cabecalho_agendar_conversao',
      hamburgerMenu: 'cta_alta-df_site_menu_agendar_conversao',
      examDetail: 'cta_alta-df_site_pagina-produto-exames_agendar_conversao',
      vaccineDetail: 'cta_alta-df_site_pagina-produto-vacinas_agendar_conversao',
    },
  }

  const [mobileDevice, setMobileDevice] = useState(true)

  const screen = typeof window !== 'undefined' ? window.screen : false

  useEffect(() => {
    if (!isMobile(screen)) {
      setMobileDevice(false)
    }
  }, [screen])

  const icon = <MapPin size={20} />

  const handleTag = ({ title, contact }) => {
    window.dataLayer = window.dataLayer || []
    window.dataLayer.push({
      event: 'menu_interaction',
      context: {
        button_label: title,
        button_contact: contact,
      },
    })
  }

  const updateSchedulingLink = ({ schedulingLink, campaignValue }) => {
    return schedulingLink.replace(/utm_campaign=[^&]+/, `utm_campaign=${campaignValue}`)
  }

  const mountContent = ({
    title,
    schedulingLink,
    wppLink,
    phoneText,
    phoneLink,
    campaignValue,
  }) => {
    const updatedSchedulingLink = updateSchedulingLink({ schedulingLink, campaignValue })

    return (
      <ButtonContainer>
        <Button
          href={updatedSchedulingLink}
          target={isExternalLink(updatedSchedulingLink)}
          onClick={() => handleTag({ title, contact: 'Agendar online' })}
        >
          Agendar online
        </Button>
        <Button
          href={wppLink}
          outline
          target="_blank"
          onClick={() => handleTag({ title, contact: 'Agendar pelo Whatsapp' })}
        >
          Agendar pelo Whatsapp
        </Button>
        <Button href={phoneLink} outline onClick={() => handleTag({ title, contact: phoneText })}>
          {phoneText}
        </Button>
      </ButtonContainer>
    )
  }

  const accordionItems = [
    {
      title: 'São Paulo',
      id: 'sp',
      icon,
      content: mountContent({
        title: 'São Paulo',
        schedulingLink: isHomeSched ? settings.home_sched_link_sp : settings.scheduling_link_sp,
        wppLink: whatsappCard.spButtonLink,
        phoneLink: phoneCard.spButtonLink,
        phoneText: phoneCard.spButtonText,
        campaignValue: campaignNameMapping.sp[origin] || 'agendamento',
      }),
    },
    {
      title: 'Rio de Janeiro',
      id: 'rj',
      icon,
      content: mountContent({
        title: 'Rio de Janeiro',
        schedulingLink: isHomeSched ? settings.home_sched_link_rj : settings.scheduling_link_rj,
        wppLink: whatsappCard.rjButtonLink,
        phoneLink: phoneCard.rjButtonLink,
        phoneText: phoneCard.rjButtonText,
        campaignValue: campaignNameMapping.rj[origin] || 'agendamento',
      }),
    },
  ]

  if (!isHomeSched) {
    accordionItems.push({
      title: 'Distrito Federal',
      id: 'df',
      icon,
      content: mountContent({
        title: 'Distrito Federal',
        schedulingLink: settings.scheduling_link_df,
        wppLink: whatsappCard.dfButtonLink,
        phoneLink: phoneCard.dfButtonLink,
        phoneText: phoneCard.dfButtonText,
        campaignValue: campaignNameMapping.df[origin] || 'agendamento',
      }),
    })
  }

  return (
    <>
      {mobileDevice ? (
        <BottomSheet isOpen={isOpen} title="Escolha onde e como deseja agendar" onClose={onClose}>
          <div>
            <Accordion items={accordionItems} defaultId={defaultRegion} />
          </div>
        </BottomSheet>
      ) : (
        <Modal isOpen={isOpen} onClose={onClose} title="Escolha onde e como deseja agendar">
          <div>
            <Accordion items={accordionItems} defaultId={defaultRegion} />
          </div>
        </Modal>
      )}
    </>
  )
}

SchedulingModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  isHomeSched: PropTypes.bool,
  defaultRegion: PropTypes.string,
  origin: PropTypes.oneOf([
    'header',
    'hamburgerMenu',
    'examShowcase',
    'vaccineShowcase',
    'examDetail',
    'vaccineDetail',
  ]).isRequired,
}
